import React, { useEffect, useState } from "react";
import axiosInstance from '../../utils/axiosInstance.js';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark, faCircleQuestion } from "@fortawesome/free-solid-svg-icons";
import { Tooltip } from "react-tooltip";
import "./popupPage.css";

/**
 * User creation dialog.
 *
 * sessionRole:
 *   Role of the currently login user, used for permission checks.
 *
 * sessionTenantName:
 *   TenantName associated with the current session, used as the parent tenant
 *   when creating sub-admins and as the tenant context for new users.
 */
const UserCreatePopupForm = ({
  onClose,
  sessionRole,                            // Current user's role.
  sessionTenantName,                      // Current tenant context
}) => {
  const fields = [
    {
      key: "email",
      display: "User Email",
      htmlType: "input",
      type: "string",
      default: "",
      tooltip: "Email for the tenant admin user",
      validate: true,
      data: true,
      roles: ["god", "admin"]
    },
    {
      key: "password",
      display: "Password",
      htmlType: "password",
      type: "string",
      default: "",
      tooltip: "Password for the admin user",
      validate: true,
      data: true,
      roles: ["god", "admin"]
    },
    {
      key: "role",
      display: "User Role",
      htmlType: "select",
      type: "string",
      default: "admin",
      tooltip: "Role of the user",
      options: ["admin"],
      validate: true,
      data: true,
      roles: ["god"]
    },
    {
      key: "role",
      display: "User Role",
      htmlType: "select",
      type: "string",
      default: "admin",
      tooltip: "Role of the user",
      options: ["admin", "subAdmin", "viewer"],
      validate: true,
      data: true,
      roles: ["admin"]
    },
    {
      key: "tenantName",
      display: "Tenant Name",
      htmlType: "input",
      type: "string",
      default: "",
      tooltip: "Unique Tenant Name, must be alphanumeric",
      validate: true,
      data: true,
      roles: ["god"]
    },
    {
      key: "tenantDesc",
      display: "Tenant Description",
      htmlType: "input",
      type: "string",
      default: "",
      tooltip: "Description of the tenant",
      validate: true,
      data: true,
      roles: ["god"]
    },
    {
      key: "deviceId",
      display: "Device ID",
      htmlType: "input",
      type: "string",
      default: "",
      tooltip: "Device ID of the device",
      validate: true,
      data: true,
      roles: ["admin"]
    },    
  ];
  

  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const newFormData = {};
    const newErrorData = {};

    for (const field of fields) {
      newFormData[field.key] = field.default;
      newErrorData[field.key] = "";
    }

    setFormData(newFormData);
    setErrors(newErrorData);
  }, []);

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [id]: value }));

    if (errors[id]) {
      setErrors((prevErrors) => ({ ...prevErrors, [id]: "" }));
    }
  };

  const validateTenantName = (tenantName) => /^[a-z0-9-]+$/.test(tenantName);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newFormData = Object.fromEntries(new FormData(e.target));
    const newErrors = {};
    let consolidatedFormData = {};

    fields.forEach((field) => {
      consolidatedFormData[field.key] = newFormData[field.key];
    });

    for (const field of fields) {
      // Skip validation for role hidden fields
      if (!canViewField(field)) {
        continue;
      }
      const value = newFormData[field.key];
      if (field.validate && !value) {
        newErrors[field.key] = `${field.display} is required`;
      } else if (field.key === "tenantName" && !validateTenantName(value)) {
        newErrors[field.key] = "Tenant Name: lowercase letters, numbers, and hyphens only. No spaces/underscore.";
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      await createTenantUser(
        consolidatedFormData, 
        {
          role: sessionRole,
          tenantName: sessionTenantName
        }
      );
    } catch (error) {
      alert(error.response?.data?.message || error.message);
      return;
    }

    alert(`User ${consolidatedFormData["email"]} added successfully!`);
    onClose();
  };

  const createTenantIfNotExist = async (tenantName, tenantDesc, session) => {

    // sub-admin will use the admin session tenantName to linked as a parent tenant
    const parentTenant =
    session.role === "admin"
      ? session.tenantName
      : null

    try {
      await axiosInstance.get(`/tenants/${tenantName}/info`);
      // Tenant info response, throw it as error as we cant duplicate the tenant
      throw new Error(`Tenant "${tenantName}" already exists.`);
    } catch (error) {
      if (error.response?.status !== 404) {
        throw error;
      }
      else
      {
        // 404 menas Tenant not exist, create it
      }
    }
    
    await axiosInstance.post("/tenants/add", {
      tenantName,
      tenantDesc,
      parentTenant
    });

    // return the tenantName that created if success
    return tenantName;
  };

  const createUser = async (data, tenantName) => {
    await axiosInstance.post("/users/add", {
      email: data.email,
      password: data.password,
      tenantName: tenantName,
      role: data.role,
      deviceId: data.deviceId
    });
  };

  const createTenantUser = async (formData, session) => {
      let tenantName;

      // Only God User and subAdmin can create Tenant
      if (canCreateTenant) {
        await createTenantIfNotExist(
          formData.tenantName,
          formData.tenantDesc,
          {
            role: session.role,
            tenantName: session.tenantName
          }
        );
        tenantName = formData.tenantName
      } else {
        tenantName = session.tenantName;
      }

      // Create the user if no error happen for the tenant creation
      await createUser(formData, tenantName);
      console.log("User created successfully.");
  }; 


  const hideLabelIfNull = (field) => {
    if (field.hidden === "null" && formData[field.key] === "") {
      return { id: "hide" };
    }
    return {};
  };

  // Can create the Tenant if login as god or the user to be create is subAdmin
  const canCreateTenant = (sessionRole === "god" || formData.role === "subAdmin");

  const canViewField = (field) => {
    // No roles defined = everyone can see it
    if (!field.roles) {
      return true;
    }

    // deviceId is only relevant when creating/editing a viewer
    if (field.key === "deviceId") {
      return formData.role === "viewer";
    }

    // godUser or subAdmin can create Tenant 
    if ((field.key === "tenantName") || (field.key === "tenantDesc"))
    {
      return canCreateTenant;
    }

    return field.roles.includes(sessionRole);
  };

  const renderInput = (field) => {
    if (field.htmlType === "select") {
      return (
        <select id={field.key} name={field.key} value={formData[field.key]} onChange={handleChange}>
          {field.options.map((option) => (
            <option key={option} value={option}>{option}</option>
          ))}
        </select>
      );
  }

    return (
      <input
        type={field.htmlType === "password" ? "password" : "text"}
        id={field.key}
        name={field.key}
        value={formData[field.key]}
        onChange={handleChange}
      />
    );
  };

  return (
    <div className="popup-overlay">
      <div className="popup">
        <div className="popup-header">
          <span className="popup-title">{canCreateTenant ? "Create Tenant and Admin" : "Create User"}</span>
          <button className="close-btn" onClick={onClose}>
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {fields
            .filter(canViewField)
            .map((field) => (
            <label key={field.key} id={field.key} {...hideLabelIfNull(field)}>
              {field.display}
              <div style={{ marginLeft: "5px" }}>
                <FontAwesomeIcon id={`${field.key}_tooltip`} icon={faCircleQuestion} />
              </div>
              {renderInput(field)}
              {errors[field.key] && <div className="error">{errors[field.key]}</div>}
              <Tooltip anchorSelect={`#${field.key}_tooltip`} place="bottom" openOnClick="true">
                {field.tooltip}
              </Tooltip>
            </label>
          ))}

          <button className="popup-submit-btn" type="submit">
            Submit
          </button>
        </form>
      </div>
    </div>
  );
};

export default UserCreatePopupForm;
